import { Signal } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { HealthStatusCheck } from '@lib/members/data-access';

export const ALLOWED_TIME_START_HOUR = 9;
export const ALLOWED_TIME_END_HOUR = 19;
export const ALLOWED_TIME_END_MINUTE = 58;
export const DEFAULT_TIME_HOUR = 19;
export const DEFAULT_TIME_MINUTE = 30;
export const MIN_LEAD_TIME_MS = 60 * 60 * 1000;

export function isPlanned(item: HealthStatusCheck, now: Date = new Date()): boolean {
  return new Date(item.scheduledFor).getTime() >= now.getTime();
}

export function isSent(item: HealthStatusCheck, now: Date = new Date()): boolean {
  return new Date(item.scheduledFor).getTime() < now.getTime();
}

export function findNextScheduledCheck(items: HealthStatusCheck[], now: Date = new Date()): Date | null {
  const upcoming = items
    .filter(item => isPlanned(item, now))
    .map(item => new Date(item.scheduledFor))
    .sort((a, b) => a.getTime() - b.getTime());

  return upcoming[0] ?? null;
}

export function getDefaultScheduleDate(now: Date = new Date()): Date {
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return tomorrow;
}

export function getDefaultScheduleTime(now: Date = new Date()): Date {
  const time = new Date(now);
  time.setHours(DEFAULT_TIME_HOUR, DEFAULT_TIME_MINUTE, 0, 0);

  return time;
}

export function combineDateAndTime(date: Date, time: Date): Date {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);

  return combined;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function getStartOfToday(now: Date = new Date()): Date {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  return start;
}

export function futureOrTodayValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as Date | null;
    if (!value) return null;

    const startOfToday = getStartOfToday();

    const candidate = new Date(value);
    candidate.setHours(0, 0, 0, 0);

    return candidate.getTime() < startOfToday.getTime() ? { pastDate: true } : null;
  };
}

export function allowedTimeRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as Date | null;
    if (!value) return null;

    const hour = value.getHours();
    const minute = value.getMinutes();

    if (hour < ALLOWED_TIME_START_HOUR) return { outOfHours: true };
    if (hour > ALLOWED_TIME_END_HOUR) return { outOfHours: true };
    if (hour === ALLOWED_TIME_END_HOUR && minute > ALLOWED_TIME_END_MINUTE) return { outOfHours: true };

    return null;
  };
}

export function todayMinOneHourValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const date = group.get('date')?.value as Date | null;
    const time = group.get('time')?.value as Date | null;
    if (!date || !time) return null;

    const now = new Date();
    if (!isSameDay(date, now)) return null;

    const combined = combineDateAndTime(date, time);
    return combined.getTime() - now.getTime() <= MIN_LEAD_TIME_MS ? { tooSoon: true } : null;
  };
}

export function noConflictOnSameDayValidator(itemsSignal: Signal<HealthStatusCheck[]>): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const date = group.get('date')?.value as Date | null;
    if (!date) return null;

    const conflict = itemsSignal().some(item => isSameDay(new Date(item.scheduledFor), date));

    return conflict ? { dayConflict: true } : null;
  };
}

export function getFormScheduleErrors(form: FormGroup): string | null {
  const errors = form.errors;
  if (errors?.['tooSoon']) return 'tooSoon';
  if (errors?.['dayConflict']) return 'dayConflict';

  const dateErrors = form.get('date')?.errors;
  if (dateErrors?.['pastDate']) return 'pastDate';

  const timeErrors = form.get('time')?.errors;
  if (timeErrors?.['outOfHours']) return 'outOfHours';

  return null;
}
